import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { messageAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  GraduationCap, 
  ArrowLeft,
  Send,
  Loader2,
  MessageSquare
} from 'lucide-react';

const MessagesPage = () => {
  const { partnerId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadConversations();
  }, []);

  useEffect(() => {
    if (partnerId) {
      loadMessages(partnerId);
    }
  }, [partnerId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadConversations = async () => {
    try {
      const response = await messageAPI.listConversations();
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (id) => {
    try {
      const response = await messageAPI.getConversation(id);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !partnerId) return;

    setSending(true);
    try {
      await messageAPI.send({ recipient_id: partnerId, content: newMessage });
      setNewMessage('');
      loadMessages(partnerId);
      loadConversations();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const getDashboardLink = () => {
    if (user?.role === 'admin') return '/admin';
    if (user?.role === 'tutor') return '/tutor-dashboard';
    return '/dashboard';
  };

  const selectedConversation = conversations.find(c => c.partner?.user_id === partnerId);

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-indigo-700" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="messages-page">
      {/* Header */}
      <header className="bg-white border-b border-stone-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(getDashboardLink())} data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <Link to="/" className="flex items-center gap-2">
              <GraduationCap className="h-8 w-8 text-indigo-700" />
              <span className="font-heading text-xl font-semibold text-slate-900">
                Messages
              </span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid md:grid-cols-3 gap-6 h-[calc(100vh-12rem)]">
          {/* Conversations List */}
          <Card className="border-stone-100 overflow-hidden">
            <div className="p-4 border-b border-stone-100">
              <h2 className="font-heading text-lg font-semibold text-slate-900">Conversations</h2>
            </div>
            <ScrollArea className="h-[calc(100%-60px)]">
              {conversations.length === 0 ? (
                <div className="p-6 text-center">
                  <MessageSquare className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-500 text-sm">No conversations yet</p>
                </div>
              ) : (
                <div className="divide-y divide-stone-100">
                  {conversations.map((conv) => (
                    <button
                      key={conv.partner?.user_id}
                      onClick={() => navigate(`/messages/${conv.partner?.user_id}`)}
                      className={`w-full p-4 text-left hover:bg-stone-50 transition-colors ${
                        partnerId === conv.partner?.user_id ? 'bg-indigo-50' : ''
                      }`}
                      data-testid={`conv-${conv.partner?.user_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={conv.partner?.picture} />
                          <AvatarFallback className="bg-indigo-100 text-indigo-700">
                            {conv.partner?.name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <h3 className="font-medium text-slate-900 truncate">
                              {conv.partner?.name}
                            </h3>
                            {conv.unread_count > 0 && (
                              <span className="w-5 h-5 rounded-full bg-indigo-700 text-white text-xs flex items-center justify-center">
                                {conv.unread_count}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-500 truncate">
                            {conv.last_message?.content}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          </Card>

          {/* Chat Area */}
          <Card className="md:col-span-2 border-stone-100 flex flex-col overflow-hidden">
            {partnerId ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b border-stone-100 flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={selectedConversation?.partner?.picture} />
                    <AvatarFallback className="bg-indigo-100 text-indigo-700">
                      {selectedConversation?.partner?.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <h2 className="font-heading text-lg font-semibold text-slate-900">
                    {selectedConversation?.partner?.name || 'Chat'}
                  </h2>
                </div>

                {/* Messages */}
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-4">
                    {messages.map((msg) => {
                      const isOwn = msg.sender_id === user?.user_id;
                      return (
                        <div
                          key={msg.message_id}
                          className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                              isOwn
                                ? 'bg-indigo-700 text-white rounded-br-md'
                                : 'bg-stone-100 text-slate-900 rounded-bl-md'
                            }`}
                          >
                            <p className="text-sm">{msg.content}</p>
                            <p className={`text-xs mt-1 ${isOwn ? 'text-indigo-200' : 'text-slate-400'}`}>
                              {format(new Date(msg.created_at), 'h:mm a')}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>

                {/* Input */}
                <form onSubmit={handleSendMessage} className="p-4 border-t border-stone-100">
                  <div className="flex items-center gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type a message..."
                      className="flex-1"
                      disabled={sending}
                      data-testid="message-input"
                    />
                    <Button 
                      type="submit" 
                      size="icon" 
                      disabled={sending || !newMessage.trim()}
                      className="rounded-full"
                      data-testid="send-btn"
                    >
                      {sending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <MessageSquare className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-heading text-xl text-slate-900 mb-2">Select a conversation</h3>
                  <p className="text-slate-500">Choose a conversation from the list to start chatting.</p>
                </div>
              </div>
            )}
          </Card>
        </div>
      </main>
    </div>
  );
};

export default MessagesPage;
